import { ChildDiscipline as DisciplineAnswer } from '@hooks/step-options/enums'
import type { QuizAnswerValue } from '@hooks/steps/types'
import type { Customer } from '@ts-types/customer/interfaces'
import type { CSCommonNotification } from '@ts-types/notification/interfaces'
import type { QuizMainStore, QuizMainAnswer, BookingElaData } from './interfaces'
import type { LinkAutologin, QuizMainAnswers, ChildrenPositionEla } from './types'
import type { Child, ChildrenLinks } from '@ts-types/child/interfaces'
import type { QuizMainAbbr } from '@stores/quiz/types'
import type { QuizMainOnboardingStep } from './enums'
import type { QuizMainStep } from '@stores/quiz/enums'
import type { BookingSlot } from '@ts-types/booking/interfaces'
import type { SelectOption } from '@ts-types/common/select/interfaces'
// helpers
import { isString } from '@helpers/string'
// config
import { answersNames } from '@hooks/analytics/config'

const setNotification = (state: QuizMainStore, payload: CSCommonNotification): void => {
  state.notification = { ...payload }

  const timeoutID = setTimeout((): void => {
    state.notification = {
      show: false,
      title: '',
      description: '',
      mode: 'info'
    }
    clearTimeout(timeoutID)
  }, (payload.mode === 'error') ? 3200 : 2200)
}

const setStep = (state: QuizMainStore, payload: QuizMainStep | null): void => {
  state.step = payload
}

const setOnboardingStep = (state: QuizMainStore, payload: QuizMainOnboardingStep | null): void => {
  state.onboardingStep = payload
}

export const actions = {
  setLoader(this: QuizMainStore, payload: boolean): void {
    this.loader = payload
  },
  setProcessing(this: QuizMainStore, payload: boolean): void {
    this.processing = payload
  },
  setModalError(this: QuizMainStore, payload: boolean): void {
    this.modalError = payload
  },
  setModalOtp(this: QuizMainStore, payload: boolean): void {
    this.modalOtp = payload
  },
  setPhoneNumber(this: QuizMainStore, payload: string | null): void {
    this.phoneNumber = payload
  },
  setOtpError(this: QuizMainStore, payload: boolean): void {
    this.otpError = payload
  },
  setLinkAutologin(this: QuizMainStore, payload: LinkAutologin): void {
    this.linkAutologin = payload
  },
  setRegisteredCustomer(this: QuizMainStore, payload: Customer | null): void {
    this.registeredCustomer = payload
  },
  setNotification(this: QuizMainStore, payload: CSCommonNotification): void {
    setNotification(this, payload)
  },
  spawnSuccess(this: QuizMainStore, payload: Pick<CSCommonNotification, 'title' | 'description'>): void {
    setNotification(this, {
      show: true,
      mode: 'success',
      title: payload.title,
      description: payload.description
    })
  },
  spawnError(this: QuizMainStore, payload: Pick<CSCommonNotification, 'title' | 'description'>): void {
    setNotification(this, {
      show: true,
      mode: 'error',
      title: payload.title,
      description: payload.description
    })
  },
  spawnWarning(this: QuizMainStore, payload: Pick<CSCommonNotification, 'title' | 'description'>): void {
    setNotification(this, {
      show: true,
      mode: 'warning',
      title: payload.title,
      description: payload.description
    })
  },
  spawnInfo(this: QuizMainStore, payload: Pick<CSCommonNotification, 'title' | 'description'>): void {
    setNotification(this, {
      show: true,
      mode: 'warning',
      title: payload.title,
      description: payload.description
    })
  },
  setQuizFinish(this: QuizMainStore, payload: boolean): void {
    this.quizFinish = payload
  },
  setOnboardingStep(this: QuizMainStore, payload: QuizMainOnboardingStep | null): void {
    setOnboardingStep(this, payload)
  },
  setLazyOnboardingStep(this: QuizMainStore, payload: QuizMainOnboardingStep | null): void {
    const timeout = setTimeout(() => {
      setOnboardingStep(this, payload)
      clearTimeout(timeout)
    }, 450)
  },
  setStep(this: QuizMainStore, payload: QuizMainStep | null): void {
    setStep(this, payload)
  },
  setLazyStep(this: QuizMainStore, payload: QuizMainStep | null): void {
    const timeout = setTimeout(() => {
      setStep(this, payload)
      clearTimeout(timeout)
    }, 450)
  },
  setHomeStep(this: QuizMainStore, payload: QuizMainStep | null): void {
    this.homeStep = payload
  },
  setExtendedSlots(this: QuizMainStore, payload: boolean): void {
    this.extendedSlots = payload
  },
  setScreenRecording(this: QuizMainStore, payload: boolean): void {
    this.screenRecording = payload
  },
  setChildrenLink(this: QuizMainStore, payload: ChildrenLinks | null): void {
    this.childrenLink = payload
  },
  setChildrenLinkEla(this: QuizMainStore, payload: ChildrenLinks | null): void {
    this.childrenLinkEla = payload
  },
  setFirstChildName(this: QuizMainStore, payload: string | null): void {
    this.firstChildName = payload
  },
  setSecondChildTab(this: QuizMainStore, payload: boolean): void {
    this.secondChildTab = payload
  },
  setSecondChildName(this: QuizMainStore, payload: string | null): void {
    this.secondChildName = payload
  },
  setGrade(this: QuizMainStore, payload: Child['grade'] | null): void {
    this.grade = payload
  },
  setFirstChildDiscipline(this: QuizMainStore, payload: DisciplineAnswer | null): void {
    this.firstChildDiscipline = payload
  },
  setSecondChildGrade(this: QuizMainStore, payload: Child['grade'] | null): void {
    this.secondChildGrade = payload
  },
  setReservedFirstTimeSlot(this: QuizMainStore, payload: string | null): void {
    this.reservedFirstTimeSlot = isString(payload)
      ? payload
      : null
  },
  setReservedSecondTimeSlot(this: QuizMainStore, payload: string | null): void {
    this.reservedSecondTimeSlot = isString(payload)
      ? payload
      : null
  },
  setAdditionalStoreLink(this: QuizMainStore, payload: string | null): void {
    this.additionalStoreLink = payload
  },
  setRedirectStoreLink(this: QuizMainStore, payload: string | null): void {
    this.redirectStoreLink = payload
  },
  setEmail(this: QuizMainStore, payload: string | null): void {
    this.email = payload
  },
  setTimezone(this: QuizMainStore, payload: string): void {
    this.timezone = payload
  },
  setTimezoneAbbr(this: QuizMainStore, payload: string): void {
    this.timezoneAbbr = payload
  },
  setAssessments(this: QuizMainStore, payload: QuizMainAnswers | null): void {
    this.assessments = payload
  },
  setAnswers(this: QuizMainStore, payload: QuizMainAnswers | null): void {
    this.answers = payload
  },
  setAnswer(this: QuizMainStore, payload: Pick<QuizMainAnswer, 'answer_name' | 'answer_title' | 'question_title'>): void {
    const state = this as unknown as QuizMainStore

    if (!state.answers) {
      state.answers = {}
    }
    state.answers[payload.answer_name] = {
      ...payload,
      question_name: payload.answer_name.replace('answer', 'question')
    }
  },
  setAssessment(this: QuizMainStore, payload: QuizMainAnswer): void {
    const state = this as unknown as QuizMainStore

    if (!state.assessments) {
      state.assessments = {}
    }
    state.assessments[payload.question_name] = {
      ...payload,
    }
  },
  setQuizAbbr(this: QuizMainStore, payload: QuizMainAbbr): void {
    this.quizAbbr = payload
  },
  setTriggerShowNote(this: QuizMainStore): void {
    this.triggerShowNote++
  },
  setZip(this: QuizMainStore, payload: string | null): void {
    this.zip = payload
  },
  setTrackedViewContent(this: QuizMainStore, payload: boolean): void {
    this.trackedViewContent = payload
  },
  setParentName(this: QuizMainStore, payload: string | null): void {
    this.parentName = payload
  },
  setTriggerOtpSuccessfullyVerified(this: QuizMainStore): void {
    this.triggerOtpSuccessfullyVerified++
  },
  setCustomerId(this: QuizMainStore, payload: number | null): void {
    this.customerId = payload
  },
  setLongBookingWindow(this: QuizMainStore, payload: boolean): void {
    this.longBookingWindow = payload
  },
  setFirstChildBookedTime(this: QuizMainStore, payload: string | null): void {
    this.firstChildBookedTime = payload
  },
  setAllSlots(this: QuizMainStore, payload: BookingSlot[]): void {
    this.allSlots = payload
  },
  setTzSlots(this: QuizMainStore, payload: BookingSlot[]): void {
    this.tzSlots = payload
  },
  setLessonDates(this: QuizMainStore, payload: SelectOption[]): void {
    this.lessonDates = payload
  },
  setLoadedSlots(this: QuizMainStore, payload: boolean): void {
    this.loadedSlots = payload
  },
  setReservedFirstTimeSlotEla(payload: string | null): void {
    const state = this as unknown as QuizMainStore
    state.reservedFirstTimeSlotEla = isString(payload)
      ? payload
      : null
  },
  setReservedSecondTimeSlotEla(payload: string | null): void {
    const state = this as unknown as QuizMainStore
    state.reservedSecondTimeSlotEla = isString(payload)
      ? payload
      : null
  },
  setSecondChildTabEla(payload: boolean): void {
    const state = this as unknown as QuizMainStore
    state.secondChildTabEla = payload
  },
  setPropsInfo(this: QuizMainStore, payload: Record<string, any>): void {
    this.propsInfo = payload
  },
  setBookingElaData(this: QuizMainStore, payload: BookingElaData | null): void {
    this.bookingElaData = payload
  },
  setChildrenPositionEla(this: QuizMainStore, payload: ChildrenPositionEla): void {
    this.childrenPositionEla = payload
  },
  setTriggerChildrenPositions(this: QuizMainStore): void {
    this.triggerChildrenPositions++
  },
  setModalEla(this: QuizMainStore, payload: boolean): void {
    this.modalEla = payload
  },
  getAnswer (payload: QuizMainStep): QuizAnswerValue | null {
    const state = this as unknown as QuizMainStore
    const answers = state.answers
    if (!answers) {
      return null
    } else {
      const key = answersNames[payload]
      return key
        ? (answers?.[key] || null)
        : null
    }
  },
  setFirstChildDisciplineMath (this: QuizMainStore, payload: boolean): void {
    this.firstChildDisciplineMath = payload
  },
  setFirstChildDisciplineReading (this: QuizMainStore, payload: boolean): void {
    this.firstChildDisciplineReading = payload
  },
  setSecondChildDisciplineMath (this: QuizMainStore, payload: boolean): void {
    this.secondChildDisciplineMath = payload
  },
  setSecondChildDisciplineReading (this: QuizMainStore, payload: boolean): void {
    this.secondChildDisciplineReading = payload
  },
  setTriggerValidateFirstChildForm (this: QuizMainStore): void {
    this.triggerValidateFirstChildForm++
  },
  setTriggerValidateSecondChildForm (this: QuizMainStore): void {
    this.triggerValidateSecondChildForm++
  },
  isValidFirstChildForm (this: QuizMainStore, disciplineAnswer: DisciplineAnswer): boolean {
    const isReading = (disciplineAnswer === DisciplineAnswer.Reading)
    const isBoth = (disciplineAnswer === DisciplineAnswer.Both)

    const validMath = !!this.reservedFirstTimeSlot
    const validReading = !!this.reservedFirstTimeSlotEla
    const validChildName = !!this.firstChildName

    if (isReading) {
      if (this.firstChildDisciplineMath && this.firstChildDisciplineReading) {
        return validChildName && (validMath && validReading)
      } else {
        return validChildName && validReading
      }
    } else if (isBoth) {
      return validChildName && (validMath && validReading)
    } else {
      if (this.firstChildDisciplineMath && this.firstChildDisciplineReading) {
        return validChildName && (validMath && validReading)
      } else {
        return validChildName &&validMath
      }
    }
  },
  isValidSecondChildForm (this: QuizMainStore, disciplineAnswer: DisciplineAnswer): boolean {
    const validMath = !!this.reservedSecondTimeSlot
    const validReading = !!this.reservedSecondTimeSlotEla
    const validChildName = !!this.secondChildName
    const validChildGrade = !!this.secondChildGrade

    if (!disciplineAnswer) {
      return validChildName && validMath && validChildGrade
    } else {
      if (this.secondChildDisciplineMath && this.secondChildDisciplineReading) {
        return validChildName && (validMath && validReading) && validChildGrade
      } else if (!this.secondChildDisciplineMath && this.secondChildDisciplineReading) {
        return validChildName && validReading && validChildGrade
      } else {
        return validChildName && validMath && validChildGrade
      }
    }
  },
  setSecondChildBooking (this: QuizMainStore, payload: boolean): void {
    this.secondChildBooking = payload
  }
}
